/* eslint-disable import/no-named-as-default */
"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { ZodSchema } from "zod";


import Button from "@/components/ui/buttons/Button";
import { MultistepFormStepProvider } from "@/contexts/MultiStepFormContext";
import { HeadingTag } from "@/utils/constants";
import StepIndicator from "@/app/[locale]/used-car-finance/components/StepIndicator";

interface Step {
  label: string;
  index: number;
}

export interface StepData {
  component: React.ComponentType<any>;
  onStepSubmit?: (data: any, allFormData?: any) => Promise<void>;
  step?: Step;
  submitStep?: boolean;
  usesCustomNextButton?: boolean;
  isSuccessStep?: boolean;
  skipSubmit?: boolean;
  skipZodValidation?: boolean;
}

interface MultistepFormProps<T extends FieldValues> {
  stepsData: StepData[];
  onSubmit: (data: any) => Promise<void>;
  stepTitles: string[];
  title: string;
  description: string;
  schemas: { [key: number]: ZodSchema };
  TitleTag?: HeadingTag;
  DescriptionTag?: React.ElementType;
  className?: string;
  stepIndicatorClassName?: string;
  indicatorWrapperClassName?: string;
  containerId?: string;
  initialValues?: Partial<T>;
  initialStep?: number;
  initialCompletedSteps?: number[];
  showSubmitOnLastStep?: boolean;
  buttonLabels?: {
    back: string;
    next: string;
    submit: string;
    uploading: string;
  };
}

// Helper function to check if any uploads are in progress
const hasUploadsInProgress = (data: any): boolean => {
  if (!data) return false;

  // Check all form fields recursively for uploads in progress
  const checkValue = (value: any): boolean => {
    // Check arrays (like image arrays from ImageUploader)
    if (Array.isArray(value)) {
      return value.some((item) => item && typeof item === "object" && item.status === "uploading");
    }

    // Check objects recursively
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return Object.values(value).some((v) => checkValue(v));
    }

    return false;
  };

  return checkValue(data);
};

export default function MultistepForm<T extends FieldValues>({
  stepsData,
  onSubmit,
  title,
  description,
  schemas,
  TitleTag = "h3",
  DescriptionTag = "p",
  className,
  stepIndicatorClassName,
  indicatorWrapperClassName,
  containerId,
  initialValues,
  initialStep = 0,
  initialCompletedSteps = [],
  showSubmitOnLastStep = false,
  buttonLabels = {
    back: "Back",
    next: "Next",
    submit: "Submit",
    uploading: "Uploading...",
  },
}: MultistepFormProps<T>) {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set(initialCompletedSteps));

  // Memoize the steps to prevent unnecessary re-renders
  const steps: Step[] = useMemo(
    () => stepsData.filter((data) => data.step).map((data) => data.step!) ?? [],
    [stepsData],
  );

  const methods = useForm<T>({
    resolver: stepsData[currentStep]?.skipZodValidation ? undefined : zodResolver(schemas[currentStep]),
    mode: "onSubmit",
    // reValidateMode: "onChange",
    defaultValues: initialValues as any,
    shouldUnregister: false,
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValidating },
    reset,
    setValue,
    watch,
  } = methods;

  // Watch the whole form to detect uploading images
  const formValues = watch();
  const uploadsInProgress = hasUploadsInProgress(formValues);

  const [formData, setFormData] = useState<T>({} as T);

  useEffect(() => {
    if (initialValues) {
      setFormData(initialValues as T);
    }
  }, [initialValues]);

  const goToStep = useCallback(
    (targetStep: number) => {
      if (targetStep >= 0 && targetStep < stepsData.length && completedSteps.has(targetStep)) {
        setCurrentStep(targetStep);

        // Scroll to container if needed
        if (containerId) {
          setTimeout(() => {
            const element = document.getElementById(containerId);
            if (element) {
              element.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        }
      }
    },
    [completedSteps, stepsData.length, containerId],
  );

  const canGoToStep = useCallback(
    (targetStep: number) => {
      return (
        targetStep >= 0 &&
        targetStep < stepsData.length &&
        (targetStep <= currentStep || completedSteps.has(targetStep))
      );
    },
    [completedSteps, currentStep, stepsData.length],
  );

  const NextStep = async (data: Partial<T>) => {
    try {
      // Early return if uploads are in progress
      if (uploadsInProgress) {
        console.log("Cannot proceed while uploads are in progress");
        return;
      }

      // Mark current step as completed
      setCompletedSteps((prev) => new Set([...prev, currentStep]));

      setIsSubmitting(true);
      const currentStepData = stepsData[currentStep];

      // Only validate fields that have been touched, unless validation is skipped
      if (!currentStepData?.skipZodValidation) {
        const touchedFields = Object.keys(methods.formState.touchedFields);
        if (touchedFields.length > 0) {
          const isValid = await methods.trigger(touchedFields as any);
          if (!isValid) {
            setIsSubmitting(false);
            return;
          }
        }
      }

      // Compute the full up-to-date form state
      const nextFormData = {
        ...(formData as any),
        ...(methods.getValues() as any),
        ...(data as any),
      } as T;

      // Persist merged form state
      setFormData(nextFormData);

      // Pass the full form state to step handlers
      if (currentStepData?.onStepSubmit) {
        await currentStepData.onStepSubmit(data, nextFormData);
      }
      if (currentStepData?.submitStep) {
        await onSubmit(nextFormData);
      }

      setCurrentStep((prev) => Math.min(prev + 1, stepsData.length - 1));

      // Scroll to top after state update with a small delay to ensure DOM is updated
      if (containerId) {
        setTimeout(() => {
          const element = document.getElementById(containerId);
          if (element) {
            element.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      }

      setIsSubmitting(false);
    } catch (error) {
      console.log(error);
      setIsSubmitting(false);
    }
  };

  const PrevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));

    if (containerId) {
      // Use requestAnimationFrame to ensure DOM has updated after state change
      requestAnimationFrame(() => {
        const element = document.getElementById(containerId);
        if (element) {
          try {
            // Get the element's position relative to the viewport
            const rect = element.getBoundingClientRect();
            const elementTop = rect.top + window.pageYOffset;

            // Account for any fixed headers or navigation (adjust offset as needed)
            const offset = 20; // You can adjust this value based on your layout

            // Scroll to the element with proper offset
            window.scrollTo({
              top: elementTop - offset,
              behavior: "smooth",
            });

            // Fallback for older browsers or if smooth scrolling fails
            setTimeout(() => {
              if (Math.abs(window.pageYOffset - (elementTop - offset)) > 50) {
                element.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                  inline: "nearest",
                });
              }
            }, 100);
          } catch (error) {
            console.warn("Scrolling failed, using fallback:", error);
            // Fallback to scrollIntoView
            element.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }
      });
    }
  };

  const StepContent = stepsData[currentStep]?.component;

  const stepUsesCustomNextButton = stepsData[currentStep]?.usesCustomNextButton ?? false;

  const isSuccessStep = stepsData[currentStep]?.isSuccessStep ?? false;

  // Button should be disabled when:
  // 1. Form is submitting OR
  // 2. Validating OR
  // 3. Uploads are in progress
  const isButtonDisabled = isSubmitting || isValidating || uploadsInProgress;

  if (!StepContent) {
    return null;
  }

  const contextValue = {
    currentStep,
    totalSteps: stepsData.length,
    goToStep,
    nextStep: handleSubmit(NextStep),
    prevStep: PrevStep,
    canGoToStep,
  };

  return (
    <MultistepFormStepProvider value={contextValue}>
      <div
        id={containerId}
        className={`mx-auto rounded-2xl bg-white p-5 md:max-w-2xl lg:max-w-4xl lg:p-7 xl:max-w-7xl ${className || ""}`}
      >
        {!isSuccessStep && (
          <>
            <TitleTag className="urbanist text-3xl font-semibold text-primary-foreground">{title}</TitleTag>
            <DescriptionTag className="inter mt-4 text-sm font-normal text-secondary-foreground">
              {description}
            </DescriptionTag>
          </>
        )}

        <FormProvider {...methods}>
          {steps.length > 0 && currentStep !== steps.length - 1 && !isSuccessStep && (
            <StepIndicator
              steps={steps}
              currentStep={currentStep}
              containerClassName={stepIndicatorClassName}
              indicatorWrapperClassName={indicatorWrapperClassName}
            />
          )}

          <StepContent
            stepIndex={currentStep}
            control={control}
            reset={reset}
            setValue={setValue}
            NextStep={handleSubmit(NextStep)}
            PrevStep={PrevStep}
            onSubmit={onSubmit}
            stepsData={stepsData}
            errors={errors}
            formData={formData}
          />
        </FormProvider>
        {(currentStep < stepsData.length - 1 || showSubmitOnLastStep) && (
          <div className="mt-4 flex justify-center gap-3 xxxs:justify-end">
            {currentStep > 0 && (
              <Button
                buttonText={buttonLabels.back}
                onClick={PrevStep}
                bgColor="primary-light"
                textColor="primary"
                className="px-9 py-3 text-center transition-all duration-300 ease-in-out hover:bg-primary hover:text-white md:max-w-fit"
                disabled={isSubmitting}
              />
            )}

            {currentStep >= 0 && currentStep < stepsData.length - 2 && !stepUsesCustomNextButton && (
              <div className="relative inline-block">
                <span className="absolute inset-0 animate-pulse-button rounded-xl bg-primary opacity-75"></span>

                <Button
                  buttonText={isButtonDisabled ? buttonLabels.uploading : buttonLabels.next}
                  onClick={handleSubmit(NextStep)}
                  bgColor="primary"
                  textColor="white"
                  className={`relative px-12 py-3 text-center transition-all duration-300 ease-in-out ${isButtonDisabled ? "cursor-not-allowed opacity-70" : "hover:bg-primary-dark"
                    } md:max-w-fit`}
                  disabled={isButtonDisabled}
                />
              </div>
            )}

            {(currentStep === stepsData.length - 2 || (showSubmitOnLastStep && currentStep === stepsData.length - 1)) &&
              !stepsData[currentStep]?.skipSubmit && (
                <Button
                  buttonText={isButtonDisabled ? buttonLabels.uploading : buttonLabels.submit}
                  onClick={handleSubmit(NextStep)}
                  bgColor="primary"
                  textColor="white"
                  className={`w-full px-9 py-3 text-center md:max-w-fit ${isButtonDisabled ? "cursor-not-allowed opacity-70" : ""
                    }`}
                  disabled={isButtonDisabled}
                />
              )}
          </div>
        )}
      </div>
    </MultistepFormStepProvider>
  );
}
